import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Bell } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { z } from 'zod';

const waitlistSchema = z.object({
  firstName: z.string().trim().min(2).max(50),
  lastName: z.string().trim().min(2).max(50),
  company: z.string().trim().max(100).optional(),
  email: z.string().trim().email().max(255),
});

interface WaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trainingSlug: string;
  trainingTitle: string;
  initialEmail?: string;
  selectedDate?: string;
}

export const WaitlistDialog = ({ open, onOpenChange, trainingSlug, trainingTitle, initialEmail, selectedDate }: WaitlistDialogProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const fr = language === 'fr';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState(initialEmail || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Sync email when initialEmail changes (e.g. guest fills email at step 2)
  useEffect(() => {
    if (initialEmail && !email) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  const handleSubmit = async () => {
    setErrors({});
    try {
      waitlistSchema.parse({ firstName, lastName, company: company || undefined, email });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errs: Record<string, string> = {};
        err.errors.forEach(e => { if (e.path[0]) errs[e.path[0] as string] = fr ? 'Champ invalide' : 'Invalid field'; });
        setErrors(errs);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('waitlist').insert({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        company: company.trim() || null,
        email: email.trim(),
        training_slug: trainingSlug,
        training_title: trainingTitle,
      });

      if (error) {
        toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: error.message });
        setIsSubmitting(false);
        return;
      }

      // Send confirmation email via Brevo
      try {
        await supabase.functions.invoke('send-waitlist-confirmation', {
          body: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            trainingTitle,
            trainingSlug,
            selectedDate: selectedDate || null,
          },
        });
      } catch (emailErr) {
        console.error('Failed to send confirmation email:', emailErr);
      }

      setSuccess(true);
      toast({
        title: fr ? 'Inscription confirmée !' : 'Registration confirmed!',
        description: fr ? 'Vous recevrez un e-mail de confirmation sous quelques minutes.' : 'You will receive a confirmation email shortly.',
      });
    } catch {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: fr ? 'Une erreur est survenue.' : 'An error occurred.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (success) {
      setFirstName('');
      setLastName('');
      setCompany('');
      setEmail('');
      setSuccess(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {success ? (
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-center">
                {fr ? 'Inscription enregistrée !' : 'Registration confirmed!'}
              </DialogTitle>
              <DialogDescription className="text-center">
                {fr
                  ? 'Vous recevrez un e-mail de confirmation dès l\'ouverture des places pour cette formation.'
                  : 'You will receive a confirmation email as soon as spots open for this training.'}
              </DialogDescription>
            </DialogHeader>
            <Button onClick={handleClose} className="w-full">
              {fr ? 'Fermer' : 'Close'}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Bell className="w-5 h-5 text-primary" />
                <DialogTitle className="text-lg">
                  {fr ? 'Places bientôt disponibles !' : 'Spots opening soon!'}
                </DialogTitle>
              </div>
              <DialogDescription>
                {fr
                  ? 'Les places pour cette formation seront bientôt ouvertes ! Inscrivez-vous ici pour être informé(e) en priorité.'
                  : 'Spots for this training will open soon! Register here to be notified first.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-sm font-medium text-foreground">{trainingTitle}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="wl-first">{fr ? 'Prénom' : 'First name'}</Label>
                  <Input id="wl-first" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={fr ? 'Jean' : 'John'} />
                  {errors.firstName && <p className="text-xs text-destructive mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="wl-last">{fr ? 'Nom' : 'Last name'}</Label>
                  <Input id="wl-last" value={lastName} onChange={e => setLastName(e.target.value)} placeholder={fr ? 'Dupont' : 'Doe'} />
                  {errors.lastName && <p className="text-xs text-destructive mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="wl-company">{fr ? 'Cabinet / Entreprise' : 'Firm / Company'} <span className="text-muted-foreground text-xs">({fr ? 'optionnel' : 'optional'})</span></Label>
                <Input id="wl-company" value={company} onChange={e => setCompany(e.target.value)} placeholder={fr ? 'Cabinet Dupont & Associés' : 'Doe & Associates LLP'} />
              </div>

              <div>
                <Label htmlFor="wl-email">Email</Label>
                <Input id="wl-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@cabinet.com" />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>

              <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full">
                {isSubmitting ? (fr ? 'Envoi...' : 'Sending...') : (fr ? 'M\'inscrire sur la liste d\'attente' : 'Join the waitlist')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
